import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import {
  LayoutDashboard,
  ClipboardList,
  Car,
  Users,
  DollarSign,
  Package,
  Wrench,
  Calendar,
  Lightbulb,
  ParkingSquare,
  BarChart2,
  LifeBuoy,
  Settings,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useAppointmentCount } from '@/hooks/useAppointments';
import { useCan } from '@/hooks/useCan';
import { canOpenRoute } from '@/lib/route-access';
import { memberRoleLabel } from '@mustauto/types/permissions';
import { useUnseenSupportReplies } from '@/hooks/useSupportTickets';
import { ThemeToggle } from './ThemeToggle';

const SIDEBAR_COLLAPSED_KEY = 'mustauto:sidebar-collapsed';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/service-orders', label: 'Ordens de Serviço', icon: ClipboardList },
  { to: '/in-shop', label: 'Carros na oficina', icon: Car },
  { to: '/team', label: 'Equipe', icon: Users },
  { to: '/finance', label: 'Financeiro', icon: DollarSign },
  { to: '/inventory', label: 'Estoque', icon: Package },
  { to: '/tool-reports', label: 'Ferramentas', icon: Wrench },
  { to: '/appointments', label: 'Agendamentos', icon: Calendar },
  { to: '/wisdom', label: 'Sabedoria', icon: Lightbulb },
  { to: '/slots', label: 'Vagas', icon: ParkingSquare },
  { to: '/reports', label: 'Relatórios', icon: BarChart2 },
  { to: '/support', label: 'Suporte', icon: LifeBuoy },
  { to: '/settings', label: 'Configurações', icon: Settings },
] as const;

function WorkshopSwitcher({ collapsed }: { collapsed: boolean }) {
  const { workshops, switchWorkshop } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = workshops.find((w) => w.isCurrent);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (collapsed) {
    const letter = (current?.name ?? workshops[0]?.name ?? 'O')[0];
    if (workshops.length <= 1) {
      return (
        <div className="text-[10px] text-sidebar-foreground/60 text-center" title={current?.name}>
          {letter}
        </div>
      );
    }
    return (
      <div ref={ref} className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="text-[10px] text-sidebar-foreground/60 hover:text-sidebar-foreground/90 transition-colors"
          title={current?.name ?? 'Selecionar oficina'}
        >
          {letter}
        </button>
        {open && (
          <div className="absolute left-full top-0 z-50 ml-2 w-56 rounded-lg border border-white/10 bg-sidebar shadow-lg">
            {workshops.map((ws) => (
              <button
                key={ws.id}
                onClick={() => {
                  if (!ws.isCurrent) {
                    switchWorkshop(ws.id);
                  }
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors',
                  ws.isCurrent
                    ? 'bg-sidebar-active/30 text-white'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-active/20 hover:text-white',
                )}
              >
                <span className="truncate flex-1">{ws.name}</span>
                {ws.isCurrent && <Check size={12} className="shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (workshops.length <= 1) {
    return (
      <div className="text-xs text-sidebar-foreground/60 truncate max-w-[160px]">
        {current?.name ?? workshops[0]?.name}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground/90 transition-colors truncate max-w-[160px]"
      >
        <span className="truncate">{current?.name ?? 'Selecionar oficina'}</span>
        <ChevronDown size={12} className={cn('shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-white/10 bg-sidebar shadow-lg">
          {workshops.map((ws) => (
            <button
              key={ws.id}
              onClick={() => {
                if (!ws.isCurrent) {
                  switchWorkshop(ws.id);
                }
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors',
                ws.isCurrent
                  ? 'bg-sidebar-active/30 text-white'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-active/20 hover:text-white',
              )}
            >
              <span className="truncate flex-1">{ws.name}</span>
              {ws.isCurrent && <Check size={12} className="shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === '[') {
        toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { collapsed, toggle };
}

export function Sidebar({
  mobileOpen = false,
  onMobileClose,
}: { mobileOpen?: boolean; onMobileClose?: () => void } = {}) {
  const { user, signOut, isDemo } = useAuth();
  const matchRoute = useMatchRoute();
  const { data: pendingCount } = useAppointmentCount();
  // Badge de estoque crítico no item "Estoque" (spec ux_estoque, Entry Point).
  const { data: stockAlerts } = useQuery<{ count: number }>({
    queryKey: ['inventory-alerts'],
    queryFn: async () => (await api.get('/inventory/alerts')).data,
    refetchInterval: 120_000,
  });
  // Resposta do suporte não tem push no browser: o badge é o aviso.
  const unseenSupportReplies = useUnseenSupportReplies(!isDemo);
  // Sessão demo é read-only e anônima: abrir chamado não faz sentido (e a rota
  // nasce bloqueada no demo-guard).
  // Tela que a pessoa não pode abrir não aparece no menu (decisão 5 da prévia:
  // fora do caminho, esconder) — a API responderia 403 e o item levaria a uma
  // tela quebrada. A regra é a permissão NA OFICINA ATIVA (`lib/route-access`),
  // a mesma da rota da API que alimenta a tela.
  const can = useCan();
  const { workshops } = useAuth();
  const currentWorkshop = workshops.find((w) => w.isCurrent);
  const roleLabel = currentWorkshop ? memberRoleLabel(currentWorkshop.role, currentWorkshop.isOwner) : 'Gestor';
  const visibleNavItems = navItems.filter(
    ({ to }) => !(to === '/support' && isDemo) && canOpenRoute(to, can),
  );
  const { collapsed, toggle } = useSidebarCollapsed();

  return (
    <aside
      className={cn(
        // Mobile (< md): fixed off-canvas drawer toggled via mobileOpen.
        // >= md: static flow column, collapsed/expanded behavior unchanged.
        'fixed inset-y-0 left-0 z-50 flex h-screen flex-col bg-sidebar text-sidebar-foreground transition-transform duration-200',
        'md:static md:z-auto md:translate-x-0 md:transition-all',
        mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex items-center border-b border-white/10 px-3 py-5">
        {collapsed ? (
          <div className="flex w-full flex-col items-center gap-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-lg font-bold">
              M
            </div>
            <WorkshopSwitcher collapsed={collapsed} />
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-3 px-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-lg font-bold">
              M
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold tracking-wide">MustAuto</div>
              <WorkshopSwitcher collapsed={collapsed} />
            </div>
          </div>
        )}
      </div>

      {/* Collapse toggle (desktop only — drawer mode has no collapse) */}
      <div className="hidden justify-end px-2 py-1 md:flex">
        <button
          onClick={toggle}
          className="rounded-md p-1.5 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
          title={collapsed ? 'Expandir sidebar ([)' : 'Recolher sidebar ([)'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 px-3 py-2">
        {visibleNavItems.map(({ to, label, icon: Icon }) => {
          const active = !!matchRoute({ to });
          const badge =
            to === '/appointments' && pendingCount ? pendingCount
            : to === '/inventory' && stockAlerts?.count ? stockAlerts.count
            : to === '/support' && unseenSupportReplies ? unseenSupportReplies
            : 0;
          return (
            <Link
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              onClick={onMobileClose}
              className={cn(
                'relative flex items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
                active
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-active/50 hover:text-white',
              )}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && label}
              {!collapsed && badge > 0 && (
                <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                  {badge}
                </span>
              )}
              {collapsed && badge > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                  {badge}
                </span>
              )}
            </Link>
          );
        })}
        <a
          href="https://mustauto.com.br/ajuda"
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? 'Ajuda' : undefined}
          className={cn(
            'relative flex items-center rounded-lg text-sm font-medium transition-colors',
            collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
            'text-sidebar-foreground/70 hover:bg-sidebar-active/50 hover:text-white',
          )}
        >
          <HelpCircle size={18} className="shrink-0" />
          {!collapsed && 'Ajuda'}
        </a>
      </nav>

      {/* User */}
      <div className="border-t border-white/10 p-4 space-y-3">
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-xs font-bold"
              title={user?.name ?? 'Gestor'}
            >
              {(user?.name ?? 'G')[0].toUpperCase()}
            </div>
            <button
              onClick={signOut}
              className="rounded-md p-2 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="truncate">
              <div className="text-sm font-medium truncate">{user?.name ?? 'Gestor'}</div>
              {/* O papel NESTA oficina, não "Gestor" fixo (Fase 3). */}
              <div className="text-xs text-sidebar-foreground/50">{roleLabel}</div>
            </div>
            <button
              onClick={signOut}
              className="rounded-md p-2 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-active hover:text-white"
              title="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
        <ThemeToggle />
      </div>
    </aside>
  );
}
